import { useRef, useState, useEffect, useCallback } from 'react';

type Point = {
  x: number;
  y: number;
};

type Rectangle = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se' | null;

type UseSpotlightRectangleProps = {
  initialRectangle?: Rectangle;
  onRectangleChange: (rectangle: Rectangle) => void;
  disabled?: boolean;
};

export const useSpotlightRectangle = ({ initialRectangle, onRectangleChange, disabled }: UseSpotlightRectangleProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<ResizeHandle>(null);
  const [startPoint, setStartPoint] = useState<Point | null>(null);
  const [currentPoint, setCurrentPoint] = useState<Point | null>(null);
  const [rectangle, setRectangle] = useState<Rectangle>(
    initialRectangle || { x1: 0.2, y1: 0.2, x2: 0.8, y2: 0.8 }
  );
  const imageRef = useRef<HTMLImageElement | null>(null);
  const MIN_SIZE = 0.05; // 5% minimum size
  const dragOffsetRef = useRef<{dx: number, dy: number} | null>(null);

  useEffect(() => {
    if (initialRectangle) {
      const { x1, y1, x2, y2 } = initialRectangle;
      const { x1: currentX1, y1: currentY1, x2: currentX2, y2: currentY2 } = rectangle;
      
      // Only update if values have actually changed
      if (x1 !== currentX1 || y1 !== currentY1 || x2 !== currentX2 || y2 !== currentY2) {
        setRectangle(initialRectangle);
      }
    }
  }, [initialRectangle, rectangle]);

  const getRelativePoint = useCallback((event: React.MouseEvent<HTMLElement>): Point => {
    if (!imageRef.current) return { x: 0, y: 0 };
    const rect = imageRef.current.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) / rect.width,
      y: (event.clientY - rect.top) / rect.height,
    };
  }, []);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (disabled || !imageRef.current) {
        return;
      }

      // Prevent default drag behavior
      event.preventDefault();
      event.stopPropagation();

      const point = getRelativePoint(event);
      setStartPoint(point);
      setCurrentPoint(point);

      // Check if click is on a resize handle
      const handleSize = 16; // Fixed size in pixels
      const { x, y } = point;
      const { x1, y1, x2, y2 } = rectangle;

      // Convert handle size to relative coordinates
      const rect = imageRef.current.getBoundingClientRect();
      const relativeHandleSize = handleSize / rect.width;

      // Calculate distances to each corner
      const distances = {
        nw: { x: Math.abs(x - x1), y: Math.abs(y - y1) },
        ne: { x: Math.abs(x - x2), y: Math.abs(y - y1) },
        sw: { x: Math.abs(x - x1), y: Math.abs(y - y2) },
        se: { x: Math.abs(x - x2), y: Math.abs(y - y2) },
      };

      // Check if click is within handleSize of any corner
      if (distances.nw.x < relativeHandleSize && distances.nw.y < relativeHandleSize) {
        setIsResizing(true);
        setResizeHandle('nw');
      } else if (distances.ne.x < relativeHandleSize && distances.ne.y < relativeHandleSize) {
        setIsResizing(true);
        setResizeHandle('ne');
      } else if (distances.sw.x < relativeHandleSize && distances.sw.y < relativeHandleSize) {
        setIsResizing(true);
        setResizeHandle('sw');
      } else if (distances.se.x < relativeHandleSize && distances.se.y < relativeHandleSize) {
        setIsResizing(true);
        setResizeHandle('se');
      } else if (x >= x1 && x <= x2 && y >= y1 && y <= y2) {
        setIsDragging(true);
        dragOffsetRef.current = { dx: x - x1, dy: y - y1 };
      }
    },
    [disabled, rectangle, getRelativePoint]
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (disabled || !imageRef.current || (!isDragging && !isResizing) || !startPoint) {
        return;
      }

      // Prevent default drag behavior
      event.preventDefault();
      event.stopPropagation();

      const point = getRelativePoint(event);
      setCurrentPoint(point);

      if (isDragging) {
        // Use dragOffsetRef to keep the mouse position relative to where the user clicked
        const offset = dragOffsetRef.current || { dx: 0, dy: 0 };
        const width = Math.abs(rectangle.x2 - rectangle.x1);
        const height = Math.abs(rectangle.y2 - rectangle.y1);
        const newX1 = Math.max(0, Math.min(1 - width, point.x - offset.dx));
        const newY1 = Math.max(0, Math.min(1 - height, point.y - offset.dy));
        let newX2 = newX1 + width;
        let newY2 = newY1 + height;
        // Ensure min size
        if (newX2 - newX1 < MIN_SIZE) newX2 = newX1 + MIN_SIZE;
        if (newY2 - newY1 < MIN_SIZE) newY2 = newY1 + MIN_SIZE;
        const newRectangle = {
          x1: newX1,
          y1: newY1,
          x2: newX2,
          y2: newY2,
        };
        setRectangle(newRectangle);
        onRectangleChange(newRectangle);
      } else if (isResizing && resizeHandle) {
        let newRectangle = { ...rectangle };
        switch (resizeHandle) {
          case 'nw':
            newRectangle = {
              x1: Math.max(0, Math.min(rectangle.x2 - MIN_SIZE, point.x)),
              y1: Math.max(0, Math.min(rectangle.y2 - MIN_SIZE, point.y)),
              x2: rectangle.x2,
              y2: rectangle.y2,
            };
            break;
          case 'ne':
            newRectangle = {
              x1: rectangle.x1,
              y1: Math.max(0, Math.min(rectangle.y2 - MIN_SIZE, point.y)),
              x2: Math.max(rectangle.x1 + MIN_SIZE, Math.min(1, point.x)),
              y2: rectangle.y2,
            };
            break;
          case 'sw':
            newRectangle = {
              x1: Math.max(0, Math.min(rectangle.x2 - MIN_SIZE, point.x)),
              y1: rectangle.y1,
              x2: rectangle.x2,
              y2: Math.max(rectangle.y1 + MIN_SIZE, Math.min(1, point.y)),
            };
            break;
          case 'se':
            newRectangle = {
              x1: rectangle.x1,
              y1: rectangle.y1,
              x2: Math.max(rectangle.x1 + MIN_SIZE, Math.min(1, point.x)),
              y2: Math.max(rectangle.y1 + MIN_SIZE, Math.min(1, point.y)),
            };
            break;
          default:
            // This should never happen due to TypeScript type checking,
            // but adding for completeness
            newRectangle = rectangle;
            break;
        }
        // Ensure min size
        if (newRectangle.x2 - newRectangle.x1 < MIN_SIZE) newRectangle.x2 = newRectangle.x1 + MIN_SIZE;
        if (newRectangle.y2 - newRectangle.y1 < MIN_SIZE) newRectangle.y2 = newRectangle.y1 + MIN_SIZE;
        setRectangle(newRectangle);
        onRectangleChange(newRectangle);
      }
    },
    [disabled, isDragging, isResizing, resizeHandle, startPoint, rectangle, onRectangleChange, getRelativePoint]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
    setStartPoint(null);
    setCurrentPoint(null);
    dragOffsetRef.current = null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, isResizing, resizeHandle, startPoint, currentPoint]);

  const getRectangleStyle = useCallback(() => {
    const { x1, y1, x2, y2 } = rectangle;
    return {
      position: 'absolute',
      left: `${x1 * 100}%`,
      top: `${y1 * 100}%`,
      width: `${(x2 - x1) * 100}%`,
      height: `${(y2 - y1) * 100}%`,
      border: '2px solid #1976d2',
      backgroundColor: 'rgba(25, 118, 210, 0.1)',
      cursor: isDragging ? 'grabbing' : 'grab',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      MozUserSelect: 'none',
      msUserSelect: 'none',
      boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.1)',
      outline: '1px solid rgba(25, 118, 210, 0.3)',
      outlineOffset: '1px',
      pointerEvents: 'auto',
      zIndex: 1,
      transition: 'none',
    };
  }, [rectangle, isDragging]);

  const getCornerDotStyle = useCallback((position: 'nw' | 'ne' | 'sw' | 'se') => {
    const positions = {
      nw: { top: '-8px', left: '-8px' },
      ne: { top: '-8px', right: '-8px' },
      sw: { bottom: '-8px', left: '-8px' },
      se: { bottom: '-8px', right: '-8px' },
    };

    return {
      position: 'absolute',
      width: '16px',
      height: '16px',
      backgroundColor: '#1976d2',
      borderRadius: '50%',
      pointerEvents: 'none',
      ...positions[position],
    };
  }, []);

  const getResizeHandleStyle = useCallback(
    (handle: ResizeHandle) => {
      if (!handle) return null;
      const { x1, y1, x2, y2 } = rectangle;
      const handleSize = 12; // px

      const coords = {
        nw: { left: `${x1 * 100}%`, top: `${y1 * 100}%` },
        ne: { left: `${x2 * 100}%`, top: `${y1 * 100}%` },
        sw: { left: `${x1 * 100}%`, top: `${y2 * 100}%` },
        se: { left: `${x2 * 100}%`, top: `${y2 * 100}%` },
      };

      return {
        position: 'absolute',
        width: handleSize,
        height: handleSize,
        backgroundColor: '#1976d2',
        border: '2px solid white',
        borderRadius: '50%',
        cursor: `${handle}-resize`,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        pointerEvents: 'auto',
        zIndex: 1,
        transition: 'none',
        ...coords[handle],
        transform: 'translate(-50%, -50%)', // Center the handle
      };
    },
    [rectangle]
  );

  // Add global mouseup event listener for robust drag/resize end
  useEffect(() => {
    if (isDragging || isResizing) {
      const onMouseUp = () => handleMouseUp();
      window.addEventListener('mouseup', onMouseUp);
      return () => {
        window.removeEventListener('mouseup', onMouseUp);
      };
    }
    return () => {};
  }, [isDragging, isResizing, handleMouseUp]);

  return {
    imageRef,
    isDragging,
    isResizing,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    getRectangleStyle,
    getResizeHandleStyle,
    getCornerDotStyle,
  };
}; 